import { CSSProperties } from "vue";

export const iframeResetStyles = {
  animation: 'none',
  animationName: 'none',
  backfaceVisibility: 'visible',
  background: 'transparent',
  backgroundAttachment: 'scroll',
  border: '0',
  borderStyle: 'none',
  borderWidth: 'medium',
  borderColor: 'inherit',
  boxShadow: 'none',
  boxSizing: 'content-box',
  color: 'inherit',
  cursor: 'auto',
  display: 'inline',
  height: 'auto',
  left: 'auto',
  margin: '0',
  maxWidth: 'none',
  maxHeight: '100dvh',
  minWidth: 'unset',
  minHeight: 'unset',
  opacity: 1,
  outline: '0',
  outlineColor: 'invert',
  outlineStyle: 'none',
  outlineWidth: 'medium',
  overflow: 'visible',
  overflowX: 'visible',
  overflowY: 'visible',
  padding: '0',
  perspective: 'none',
  position: 'static',
  top: 'auto',
  transform: 'none',
  transition: 'none',
  visibility: 'visible',
  whiteSpace: 'normal',
  widows: 0,
  width: 'auto',
  zIndex: 'auto',
};

// we start with a frame of 0x0px
// the onmount will resize the frame to the content
const initialFrameStyles = {
  position: 'absolute',
  bottom: '0px',
  right: '0px',
  width: '0px',
  height: '0px',
  overflow: 'hidden'
}

/**
 *
 * @param {object} style
 * @returns {string}
 */

function toCssString(style) {
  return Object.keys(style).reduce((acc, key) => (
    acc + key.split(/(?=[A-Z])/).join('-').toLowerCase() + ':' + style[key] + ';'
  ), '');
}

function createCSSLinkElement(options) {
  /* const { id, sri, href } = options;
  const idAttr = `id="${id}"`
  const sriPart = `integrity="${sri}" crossorigin="anonymous"`
  return `<link ${id ? idAttr : ''} rel="stylesheet" href="${href}" ${sri ? sriPart : ''} >` */
  const { id, sri, href } = options;
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href

  if (id) {
    link.id = id
  }
  if (sri) {
    link.integrity = sri
    link.crossOrigin = 'anonymous'
  }

  return link
}

function createScriptTagElement(options) {
  /* const { id, src, sri, type } = options;
  const idAttr = `id="${id}"`
  const typeAttr = `type="${type}"`
  const sriAttr = `integrity="${sri}" crossorigin="anonymous"`
  return `<script ${id ? idAttr : ''} ${type ? typeAttr : ''} src="${src}" ${sri ? sriAttr : ''}></script>` */
  const { id, src, sri, type } = options;
  const scriptTag = document.createElement('script')
  scriptTag.src = src
  if (id) {
    scriptTag.id = id
  }
  if (sri) {
    scriptTag.integrity = sri
    scriptTag.crossOrigin = 'anonymous'
  }
  if (type) {
    scriptTag.type = type
  }
  return scriptTag
}

function buildIframe(iframe, cssFiles, scriptFiles, bodyStyles, attributes) {
  const innercontainer = document.createElement('div')
  innercontainer.id = 'moinai-container'
  innercontainer.style.height = '100%'
  innercontainer.style.width = '100%'

  /* console.log('---> ', iframe, cssFiles, scriptFiles, bodyStyles, attributes, iframe.contentDocument) */
  iframe.contentDocument.body.appendChild(innercontainer)
  iframe.contentDocument.height = '100%'

  const bStyles = `${bodyStyles.map((item) => toCssString(item)).join(' ')}`
  iframe.contentDocument.body.style = bStyles

  attributes.forEach((attr) => {
    iframe.contentDocument.body.setAttribute(attr.attrName, attr.value)
  })

  cssFiles.forEach((css) => {
    const link = createCSSLinkElement(css)
    iframe.contentDocument.head.appendChild(link)
  })

  scriptFiles.forEach((script) => {
    const scriptTag = createScriptTagElement(script)
    iframe.contentDocument.body.appendChild(scriptTag)
    /* iframe.contentDocument.head.appendChild(scriptTag) */
  })
}


function createIframeElement(container: HTMLElement, id: string, cssFiles: CSSLink[], scriptFiles: Script[], innerBodyStyles: CSSProperties[], iframeStyles: CSSProperties[], attributes: Attributes[], title: string) {
  const iframe = document.createElement('iframe');
  iframe.id = id;
  iframe.title = title;
  iframe.title = id;
  iframe.classList.add(id);
  iframe.name = id;
  container.appendChild(iframe);
  const newStyles = Object.assign({}, iframeResetStyles, initialFrameStyles, ...iframeStyles)
  Object.assign(iframe.style, newStyles)
  return new Promise(async (resolve) => {
    setTimeout(async () => {
      /* iframe.srcdoc = buildSrcdocString(cssFiles, scriptFiles, innerBodyStyles, attributes) */
      await buildIframe(iframe, cssFiles, scriptFiles, innerBodyStyles, attributes)
      /* console.log('iframe created', iframe.contentDocument) */
      resolve(iframe)
    }, 300);

  })
}


type CSSLink = {
  id: string,
  href: string,
  sri?: string
}

type Script = {
  id: string,
  src: string,
  sri?: string,
  type?: string
}

type Attributes = {
  attrName: string,
  value: string
}

export class IframeBuilder {
  private _id: string;
  private _iframetitle: string;
  private _css: CSSLink[];
  private _scripts: Script[];
  private _frameStyles: CSSProperties[];
  private _innerBodyStyles: CSSProperties[];
  private _attributes: Attributes[];

  constructor(id: string, options = {
    title: 'moinai-iframe',
    name: 'moinai-iframe'
  }) {
    const title = options.title
    this._id = id
    // accessability title to the iframe
    this._iframetitle = title
    this._css = []
    this._scripts = []
    this._frameStyles = []
    this._attributes = []
    this._innerBodyStyles = []
  }

  /**
   *
   * adds an attribute to the inner body tag of the iframe element
   * @example
   * .addInnerAttribute('data-example', 'example-value')
   * will add the attribute to the body element
   * <body data-example="example-value" >
   */
  addInnerAttribute(attrName: string, value: string) {
    this._attributes.push({
      attrName,
      value
    })
    return this
  }

  /**
   * adds an html Link element to the iframe head
   */
  addCSS(css: CSSLink) {
    this._css.push(css)
    return this;
  }

  /**
   * adds an html Link element to the iframe head
   */
  addScript(script: Script) {
    this._scripts.push(script)
    return this;
  }

  /**
   * Add styles to the iframe element
   *
   * @example
   * .addIFrameStyles({
   * position: 'absolute',
   * bottom: '0px',
   * right: '0px',
   * width: '100vw',
   * height: '100vh',
   * zIndex: '100000',
   * overflow: 'hidden'
   * })
   *
   * creates following iframe tag
   * <iframe style="position: absolute; bottom: 0px; right: 0px; width: 100vw; height: 100vh; z-index: 100000; overflow: hidden">
   */

  addIFrameStyles(styles: CSSProperties) {
    this._frameStyles.push(styles)
    return this;
  }

  /**
   * Add styles to the inner body of the iframe into the Style tag
   *
   * @example
   * .addInnerBodyStyles({
   *  margin: '20px',
   *  padding: '20px',
   *  backgroundColor: 'red'
   * })
   *
   * creates following body tag
   * <body style="margin: 20px; padding: 20px; background-color: red">
   */
  addInnerBodyStyles(styles: CSSProperties) {
    this._innerBodyStyles.push(styles)
    return this;
  }

  /**
   * Injects the iframe into a container and retruns a promise with the iframe element that was injected
   * @param {HTMLElement} container
   * @returns {Promise<HTMLElement>}
   *
   *  The promise resolves with the iframe element that was injected into the container
   *  either after the load event was fired or after a timeout of 100ms
   */

  async injectInto(container) {
    return new Promise(async (resolve) => {
      // create the iframe
      const iframe = await createIframeElement(container, this._id, this._css, this._scripts, this._innerBodyStyles, this._frameStyles, this._attributes, this._iframetitle)
      // prepare two promises one with the iframe load event
      // one with a timeout of 100ms
      // we got tha cases with FF where the load event wasn't fired reliably
      // therefore we create a timeout as fallback
      // we run in a timing issue with the load event
      // therefore we use the timeout for now and will investigate further
      //const timeoutPromise = new Promise((innerresolve) => setTimeout(() => { innerresolve('timeout') }, 100));
      /* const loadEventPromise = new Promise((innerresolve) => {
          iframe.addEventListener('load', () => innerresolve('load event'))
      }); */
      // append the iframe to the container
      //container.appendChild(iframe);
      resolve(iframe)
      // return the iframe once the first promise is resolved
      /* Promise.race([timeoutPromise])
        .then(() => {
          resolve(iframe)
        }) */
    })
  }
}
